import React, { useState } from 'react';
import { X, FileInput, Copy, Check, AlertTriangle } from 'lucide-react';

interface InsertToContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (content: string, asNote: boolean) => Promise<void>;
  content: string;
  contractTitle: string;
}

export const InsertToContractModal: React.FC<InsertToContractModalProps> = ({
  isOpen,
  onClose,
  onInsert,
  content,
  contractTitle
}) => {
  const [editedContent, setEditedContent] = useState(content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [insertType, setInsertType] = useState<'note' | 'clipboard'>('note');

  if (!isOpen) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInsert = async () => {
    setIsSubmitting(true);
    try {
      if (insertType === 'clipboard') {
        await navigator.clipboard.writeText(editedContent);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
          onClose();
        }, 1000);
      } else {
        await onInsert(editedContent, true);
        onClose();
      }
    } catch (err) {
      console.error('Failed to insert:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <FileInput className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Insert to Contract
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {contractTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Insert Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              How would you like to save this?
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setInsertType('note')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  insertType === 'note'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <p className="font-medium text-gray-900 dark:text-white">Add as Contract Note</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Save as a note attached to this contract
                </p>
              </button>
              <button
                onClick={() => setInsertType('clipboard')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  insertType === 'clipboard'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <p className="font-medium text-gray-900 dark:text-white">Copy to Clipboard</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Copy to paste elsewhere manually
                </p>
              </button>
            </div>
          </div>

          {/* Content Editor */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Content to Insert
              </label>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3 text-green-500" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={12}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              placeholder="Edit the content before inserting..."
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {editedContent.length} characters • You can edit the content before inserting
            </p>
          </div>

          {/* AI Attribution Notice */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 dark:text-amber-300">
              <p className="font-medium">AI-Generated Content</p>
              <p className="mt-0.5">
                This content was generated by AI and should be reviewed by a legal professional
                before use in official documents.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={isSubmitting || !editedContent.trim()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{insertType === 'clipboard' ? 'Copying...' : 'Saving...'}</span>
              </>
            ) : (
              <>
                {insertType === 'clipboard' ? (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy to Clipboard</span>
                  </>
                ) : (
                  <>
                    <FileInput className="w-4 h-4" />
                    <span>Add as Note</span>
                  </>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsertToContractModal;
